import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { cx } from "@/lib/cx";
import { chordLabel, useCollapsed, useCollapseHotkeys } from "@/lib/useCollapsed";
import { ChevronLeftIcon, ChevronRightIcon } from "@/ui/icons";
import { SearchBar } from "@/search/SearchBar";
import { layoutFor, matchRoute, type LayoutIntent } from "@/routes";
import { enterProps } from "@/ui";
import { SIDEBAR_QUERY, useMediaQuery } from "@/ui/overlay/useMediaQuery";
import { AccountMenu } from "./AccountMenu";
import { ConnectionBanner } from "./ConnectionStatus";
import { NavItems } from "./NavItems";
import { useUiSounds } from "./useUiSounds";
import { FieldScene } from "@/ui/jrpg/FieldScene";
import { applySkin } from "@/ui/eleven/skin";
import { useSkin } from "@/ui/eleven/useSkin";
import "@/ui/ps1/ps1.css";
import "@/ui/rail-fold.css";
import "./shell.css";

/**
 * Every page inside the shell wears the skin picked in More → Appearance
 * (ui/eleven/skin.ts): Eleven Light, Eleven Warm or the JRPG field. Under the
 * JRPG skin these routes keep its windows but draw no field behind them:
 * Kanban City is its own 3D world.
 */
export const NO_FIELD_ROUTES: ReadonlySet<string> = new Set(["kanban-city"]);

/**
 * The persistent PS1 frame: a text command rail from 720px up, and a
 * five-item bottom bar below it. A desktop screen sits inside the black bezel;
 * the phone uses its own physical bezel and keeps the screen edge to edge.
 *
 * Routes still own their layout intent and scrolling. The shell publishes
 * `--busee-page-inset` for their toolbars, `--busee-chrome-inset-end` for the
 * phone account corner, and `--busee-tabbar-h` for fixed undo toasts.
 */

/**
 * How much room the floating account corner needs beyond the page's own
 * trailing inset. The connection dot lives on that same 44px button.
 */
const CHROME_INSET_END = "2.75rem";

/**
 * What each layout intent means in classes.
 *
 * `measure` and `wide` scroll the page and cap the column, left-aligned.
 * `full-bleed` does neither: the shell stops scrolling (`overflow-hidden`) and
 * the feature receives a `min-h-0` box the exact height of the viewport, so it
 * can hold sticky lane headers over independently scrolling lanes.
 *
 * Every inner box uses the same intent-level recipe: `px-inset pt-pad-tight
 * pb-pad`. It also publishes the horizontal inset so a toolbar can cancel the
 * gutter, while the toolbar cancels the top pad itself to remain flush with the
 * scroller edge.
 */
const LAYOUTS: Record<LayoutIntent, { main: string; inner: string }> = {
  measure: {
    main: "overflow-y-auto",
    inner:
      "w-full max-w-[47.5rem] [--busee-page-inset:var(--spacing-inset)] px-inset pt-pad-tight pb-pad",
  },
  wide: {
    main: "overflow-y-auto",
    inner:
      "w-full max-w-[75rem] [--busee-page-inset:var(--spacing-inset)] px-inset pt-pad-tight pb-pad",
  },
  "full-bleed": {
    // `<main>` becomes the flex context so the feature's box gets its height
    // from the layout rather than from a percentage — `h-full` inside an
    // `overflow-hidden` block is exactly the kind of thing that resolves to
    // zero on one browser and not another.
    main: "flex flex-col overflow-hidden",
    inner:
      "flex min-h-0 min-w-0 w-full flex-1 flex-col [--busee-page-inset:var(--spacing-inset)] px-inset pt-pad-tight pb-pad",
  },
};

export function AppShell({ children, backdrop }: { children: ReactNode; backdrop?: ReactNode }) {
  const { pathname } = useLocation();
  const layout = layoutFor(pathname);
  // A route entry is a surface change, not a change to that surface's URL
  // state. Keeping this stable for `/tasks/:date` and `/workout/:date` avoids
  // remounting a screen — and losing an open editor or focused control — while
  // still replaying the page entry when the user changes sections.
  const routeKey = matchRoute(pathname)?.path ?? pathname;
  const skin = useSkin();
  const field = skin === "jrpg" && !NO_FIELD_ROUTES.has(routeKey);
  // On <html> rather than this element: peeks, menus and dialogs portal out of
  // the shell and must wear the same skin. Layout effect, so the first paint
  // is already skinned.
  useLayoutEffect(() => {
    applySkin(skin);
  }, [skin]);
  // Unconditional, even though a native root transition already owns the
  // arrival where the engine has one. Deciding it here — from a flag that is
  // only true during the transition's commit — meant the *next* render put the
  // attribute back on a page that had already arrived, and the whole screen
  // rose and faded in again a beat after the browser's crossfade ended. The
  // suppression is a DOM seal written once by `lib/motion`'s
  // `sealEnterAnimations`, not a prop this component recomputes.
  const routeEnter = enterProps("page");
  const hasSidebar = useMediaQuery(SIDEBAR_QUERY);
  useUiSounds();
  useCollapseHotkeys();
  // `AppShell` survives navigation, so these flags really are once per app
  // boot. They also prevent a desktop/mobile breakpoint flip from replaying a
  // navigation entrance that the user has already seen.
  const sidebarMounted = useRef(false);
  const compactNavMounted = useRef(false);
  const enterSidebar = hasSidebar && !sidebarMounted.current;
  const enterCompactNav = !hasSidebar && !compactNavMounted.current;

  useEffect(() => {
    if (hasSidebar) {
      sidebarMounted.current = true;
    } else {
      compactNavMounted.current = true;
    }
  }, [hasSidebar]);

  // Published for `ui/compact/Toolbar`, which reserves the corner in its bar.
  // Unset rather than zeroed on a desktop, so the toolbar's own `0px`
  // fallback is what applies and there is one source of that default.
  useEffect(() => {
    const root = document.documentElement;
    if (hasSidebar) {
      root.style.removeProperty("--busee-chrome-inset-end");
    } else {
      root.style.setProperty("--busee-chrome-inset-end", CHROME_INSET_END);
    }
    return () => {
      root.style.removeProperty("--busee-chrome-inset-end");
    };
  }, [hasSidebar]);

  return (
    <div
      className={cx("pixel-shell flex h-full min-h-0 text-ink", hasSidebar ? "pixel-shell-desktop flex-row" : "flex-col")}
      // The status bar's inset, on a PWA installed to a home screen with
      // `viewport-fit=cover`. Zero everywhere else, including on a desktop.
      style={hasSidebar ? undefined : { paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {hasSidebar ? <Sidebar enterOnBoot={enterSidebar} /> : null}

      <div className="pixel-screen ps1-backdrop relative isolate flex min-h-0 min-w-0 flex-1 flex-col">
        {backdrop}
        {field ? <FieldScene /> : null}
        {/* Compact surfaces own their Material top app bar. Universal search
            stays in the desktop shell, where it has room to remain universal
            without creating a second toolbar above every phone screen. */}
        {hasSidebar ? <GlobalSearchBar /> : null}
        <ConnectionBanner />

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {/* No peek inset here any more. The peek used to be a rail on the
              right edge and published its width so this element could shrink
              and let a wide board sit beside it; it is a centred panel now, so
              there is nothing to sit beside and nothing to inset. */}
          <main data-layout={layout} data-route={routeKey} className={cx("min-h-0 min-w-0 flex-1", LAYOUTS[layout].main)}>
            <div key={routeKey} {...routeEnter} className={LAYOUTS[layout].inner}>
              {children}
            </div>
          </main>

          {hasSidebar ? null : <ChromeCorner />}
        </div>
      </div>

      {hasSidebar ? null : <TabBar enterOnBoot={enterCompactNav} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The universal search, pinned above every surface.
 *
 * It lives here rather than inside any one feature because it reaches across all
 * of them — a project, an inbox item, a task, a board card — and because the
 * owner asked for one field at the very top of every page. Being a `shrink-0`
 * strip *outside* `<main>` is what pins it: the surface scrolls beneath it, its
 * own compact toolbar sticking to the top of the scroller just below.
 *
 * A transparent strip keeps the sky visible around one dark pill field.
 * Search results stay above the sticky page toolbar, with their opaque menu
 * surface preserving readable results over both scenes and content.
 *
 * The field is capped and left-aligned on the same inset the page content uses,
 * so its left edge lines up with the title below it instead of stretching the
 * full width of a 1200px surface.
 */
function GlobalSearchBar() {
  return (
    <div className="pixel-global-search relative z-30 flex h-14 shrink-0 items-center px-pad-tight sm:px-pad">
      <div className="w-full max-w-xl">
        <SearchBar inputClassName="pointer-coarse:min-h-tap" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The command rail. Folded (⌘B / Ctrl+B, remembered on this device) it keeps only
 * the icons, so every section stays one click away; the width animates.
 */
function Sidebar({ enterOnBoot }: { enterOnBoot: boolean }) {
  const [collapsed, toggle] = useCollapsed("sidebar");
  const label = `${collapsed ? "กางแถบเมนู" : "พับแถบเมนู"} (${chordLabel(false)})`;
  return (
    <aside className="pixel-rail flex min-h-0 shrink-0 flex-col" data-collapsed={collapsed || undefined}>
      <NavItems
        orientation="sidebar"
        enterOnBoot={enterOnBoot}
        footer={
          <>
            <button type="button" className="rail-fold focus-inset" onClick={toggle} title={label} aria-label={label} aria-pressed={collapsed}>
              {collapsed ? <ChevronRightIcon className="size-4" /> : <ChevronLeftIcon className="size-4" />}
            </button>
            <AccountMenu variant="icon" side="right" align="end" className="pixel-account-orb" />
          </>
        }
      />
    </aside>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The phone's app-level chrome: one account button with its connection dot
 * anchored to the corner, and nothing else.
 *
 * It **floats** in the trailing corner of whatever is at the top of the screen
 * rather than sitting in a bar of its own. The surface toolbar reserves this
 * corner and supplies the material beneath it, which is why the phone build no
 * longer spends a separate strip on chrome that never said where you were.
 *
 * `pointer-events-none` on the wrapper: the corner is `absolute` over the
 * content, and only its account control may swallow a tap. Everything around
 * it still belongs to the page.
 */
function ChromeCorner() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex h-16 items-center justify-end gap-1 pe-pad">
      <AccountMenu variant="icon" side="bottom" align="end" className="pointer-events-auto" />
    </div>
  );
}

/**
 * The bottom tab bar, and the one CSS variable the shell owes the overlay layer.
 *
 * `--busee-tabbar-h` is read by the toast viewport so an undo toast docks
 * *above* the tabs instead of underneath them — the toast is `position: fixed`
 * and cannot see this element any other way. It is measured rather than
 * hard-coded because the bar's height includes the home-indicator inset, which
 * differs per device and changes when the phone is rotated.
 *
 * The bar is **in flow**, a `shrink-0` sibling of the column that holds
 * `<main>` — so it is chrome *beside* the scroller, not a layer over it, and
 * nothing ever passes behind it. Its opaque black background is the desktop
 * rail laid sideways; the sky ends above it without spending a blur layer.
 */
function TabBar({ enterOnBoot }: { enterOnBoot: boolean }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty("--busee-tabbar-h", `${element.offsetHeight}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);

    return () => {
      observer.disconnect();
      // Unset, not zero: the toast's fallback is "no tab bar", and leaving a
      // stale height behind would float every desktop toast off the bottom.
      root.style.removeProperty("--busee-tabbar-h");
    };
  }, []);

  return (
    <div
      ref={ref}
      className="pixel-tabbar shrink-0 pb-[env(safe-area-inset-bottom,0px)]"
    >
      <NavItems orientation="bar" enterOnBoot={enterOnBoot} />
    </div>
  );
}
